import { useRef, useState } from "react";
import { HeaderWrapper } from "./HeaderWrapper";
import { FilterIcon } from "~/assets/icons/Filter";
import type {
  CategoryCalculatedProperties,
  Category,
} from "../../../../types/Category";
import type { Brand } from "../../../../types/Brand";
import { PlusIcon } from "~/assets/icons/Plus";
import { CheckBoxIcon } from "~/assets/icons/CheckBox";
import { BigButton } from "../BigButton";
import { useModal } from "~/context/Modal";
import { useCloseOnClickOutside } from "~/hooks/useCloseOnClickOutside";
import { useShopSearchParams } from "~/hooks/useShopSearchParams";

export const Filter = ({
  categories,
  brands,
  availableCount,
}: {
  categories: Array<CategoryCalculatedProperties & Category>;
  brands: Array<Brand>;
  availableCount: number;
}) => {
  const { filterVisible, setFilterVisible } = useModal();
  const { updateSearchParams, clearSearchParams, selected } =
    useShopSearchParams();
  const [show, setShow] = useState<
    undefined | "brands" | "categories" | "subcategories"
  >(undefined);
  const activeCategory = categories.find((c) =>
    selected.categories.some((s) => s === c.name),
  );
  const ref = useRef<HTMLDivElement>(null);
  const title = "Opna síu";

  useCloseOnClickOutside(() => {
    setFilterVisible(false);
  }, ref);

  return (
    <div className="h-full cursor-pointer" ref={ref}>
      <HeaderWrapper onClick={() => setFilterVisible(!filterVisible)}>
        <div className="mr-4 flex flex-row">{title}</div>
        <FilterIcon />
      </HeaderWrapper>
      {filterVisible && (
        <div className="fixed bottom-0 left-0 z-30 flex h-[80vh] w-screen flex-col justify-between border-t-0.5 bg-white px-8 pt-8">
          <div className="w-full">
            <div className="flex flex-row items-center justify-between">
              <div className="text-lg font-semibold">Sía</div>
              <div
                className="cursor-pointer text-base"
                onClick={() => clearSearchParams()}
              >
                Hreinsa síu
              </div>
            </div>
            <div className="w-full">
              <FilterOption
                title="Vörumerki"
                items={brands
                  .sort((a, b) => a.name.localeCompare(b.name))
                  .map((b) => {
                    return { id: b.slug, name: b.name };
                  })}
                selected={selected.brands}
                show={show === "brands"}
                toggleShow={() => {
                  setShow((s) => (s === "brands" ? undefined : "brands"));
                }}
                onClick={(id) => {
                  updateSearchParams("brand", id);
                }}
              />
              <FilterOption
                title="Vöruflokkar"
                items={categories
                  .filter((c) => c.mainCategory)
                  .map((c) => {
                    return { id: c.slug, name: c.name };
                  })}
                selected={selected.categories}
                show={show === "categories"}
                toggleShow={() => {
                  setShow((s) =>
                    s === "categories" ? undefined : "categories",
                  );
                }}
                onClick={(id) => {
                  updateSearchParams("category", id);
                }}
              />
              <FilterOption
                title="Undirflokkar"
                items={categories
                  .filter(
                    (c) =>
                      !c.mainCategory &&
                      (activeCategory === undefined ||
                        c.parentId === activeCategory.id),
                  )
                  .map((c) => {
                    return { id: c.slug, name: c.name };
                  })}
                selected={selected.subcategories}
                show={show === "subcategories"}
                toggleShow={() => {
                  setShow((s) =>
                    s === "subcategories" ? undefined : "subcategories",
                  );
                }}
                onClick={(id) => {
                  updateSearchParams("subcategory", id);
                }}
              />
            </div>
          </div>
          <div className="relative bottom-8 mt-8">
            <BigButton
              onClick={() => {
                setFilterVisible(false);
              }}
            >
              {`Skoða ${availableCount} vörur`}
            </BigButton>
          </div>
        </div>
      )}
    </div>
  );
};

const FilterOption = ({
  title,
  items,
  selected,
  show,
  toggleShow,
  onClick,
}: {
  title: string;
  items: { id: string; name: string }[];
  selected?: string[];
  show: boolean;
  toggleShow: () => void;
  onClick: (id: string) => void;
}) => {
  return (
    <div className="my-4 w-full">
      <div
        onClick={() => {
          toggleShow();
        }}
        className="flex w-full flex-row items-center justify-between text-xl font-semibold"
      >
        <div className=" cursor-pointer">
          {title}
          {selected && selected.length > 0 ? " *" : ""}
        </div>

        <PlusIcon type={show ? "Minus" : "Plus"} />
      </div>
      {show && (
        <div className="max-h-[35vh] overflow-auto">
          {items.map((item) => {
            return (
              <div
                key={item.id}
                className="my-4 flex flex-row items-center"
                onClick={() => onClick(item.id)}
              >
                <div className="mr-4">
                  <CheckBoxIcon
                    checked={selected?.includes(item.id) || false}
                  />
                </div>
                <div>{item.name}</div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};
